import { oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { DataModelSchemaVersion } from "@buildwithflux/models";
import { InvalidDocumentError, OutOfRangeError } from "./errors";
/**
 * Contains the default implementations of the FirestoreDocumentRepositoryStrategy
 *
 * The default implementation doesn't do much except the simplest thing possible:
 *  - It directly gets queries and document refs from Firestore
 *  - It doesn't decorate or filter the configs collection
 *  - It doesn't check schema versions or perform migrations
 *
 * Previously called WithoutMigrationsGetDocumentStrategy
 */
export class DefaultDocumentRepositoryStrategy {
    /**
     * Most strategies don't want to filter the configs subcollection (some, like the user context strategy do, though)
     */
    decorateConfigsCollection(configsCollection, _baseDocumentData) {
        return configsCollection;
    }
    /**
     * Most strategies don't check the schema version when returning the base document, and they don't migrate versions
     */
    async decorateBaseDocument(docRef) {
        return oneFromSnapshot(await this.getFromFirestore(docRef), () => undefined);
    }
    async getFromFirestore(ref) {
        return await ref.get();
    }
}
/**
 * The user context strategy, which migrates the document, if necessary, on read, and filters the configs subcollection to
 * just the relevant configs for the current user
 */
export class UserContextDocumentRepositoryStrategy extends DefaultDocumentRepositoryStrategy {
    functions;
    currentUserUid;
    constructor(functions, currentUserUid) {
        super();
        this.functions = functions;
        this.currentUserUid = currentUserUid;
    }
    /**
     * Decorate the configs collection
     *
     * History: we used to fetch all configs (from all users) for a document, but now we only fetch the user's and the
     * owner's ("since new users on the doc inherit the config of the owner")
     */
    decorateConfigsCollection(configsCollection, baseDocumentData) {
        return configsCollection.where("data.user_uid", "in", [this.currentUserUid, baseDocumentData.owner_uid].filter(Boolean));
    }
    /**
     * Check the schema version in the metadata of the written document and migrate it if necessary
     */
    async decorateBaseDocument(docRef) {
        let document = await oneFromSnapshot(await docRef.get(), () => undefined);
        // No such document
        if (document === undefined) {
            return undefined;
        }
        // We treat undefined versions as if they are a very old version
        const schemaVersion = document.metadata.schemaVersion ?? 0;
        // Early exit: success because we are on the latest known version
        if (schemaVersion === DataModelSchemaVersion) {
            return document;
        }
        // Early exit: failure because we aren't attempting compatibility with old client code
        if (schemaVersion > DataModelSchemaVersion) {
            throw new OutOfRangeError("Refresh this window to get the latest version, this document has been migrated and can't be used in older versions.");
        }
        if (!document.metadata?.migrationAborted) {
            // Do the migrations because we're behind
            await this.functions.schemaVersion({
                documentUid: document.documentData.uid,
                targetVersion: DataModelSchemaVersion,
            });
            // Re-retrieve the base document after the migrations have completed
            document = oneFromSnapshot(await docRef.get(), () => undefined);
        }
        // If a migration was aborted (either just now, or previously), refuse to load the document under the default strategy
        if (document?.metadata?.migrationAborted) {
            throw new InvalidDocumentError("This document failed to migrate to the latest version. Please report this as a bug so we can help you get back on track.");
        }
        return document;
    }
}
//# sourceMappingURL=strategy.js.map